import { createFileRoute, notFound } from "@tanstack/react-router";
import { getInsight, insightSlugs } from "@/lib/insights";
import { InsightShell } from "@/components/insights/InsightShell";
import { OG_IMAGE, absolute, hreflangLinks } from "@/lib/site";
import { graph, articleSchema, faqPage, breadcrumbList } from "@/lib/schema";

export const Route = createFileRoute("/insights/$slug")({
  beforeLoad: ({ params }) => {
    if (!insightSlugs.includes(params.slug)) throw notFound();
  },
  head: ({ params }) => {
    const entry = getInsight(params.slug);
    if (!entry || !insightSlugs.includes(params.slug)) return {};
    const m = entry.meta;
    const url = absolute(`/insights/${m.slug}`);
    return {
      meta: [
        { title: m.metaTitle.en },
        { name: "description", content: m.description.en },
        { property: "og:title", content: m.metaTitle.en },
        { property: "og:description", content: m.description.en },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: m.image ? absolute(m.image.src) : OG_IMAGE },
        { name: "twitter:image", content: m.image ? absolute(m.image.src) : OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/insights/${m.slug}`)],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            graph(
              articleSchema({
                path: `/insights/${m.slug}`,
                headline: m.title.en,
                description: m.description.en,
                datePublished: m.datePublished,
                dateModified: m.dateModified,
                image: m.image ? absolute(m.image.src) : undefined,
                inLanguage: "en",
              }),
              faqPage(
                `/insights/${m.slug}`,
                m.faq.map((f) => ({ q: f.q.en, a: f.a.en })),
              ),
              breadcrumbList([
                { name: "Home", path: "/" },
                { name: "Insights", path: "/insights" },
                { name: m.title.en, path: `/insights/${m.slug}` },
              ]),
            ),
          ),
        },
      ],
    };
  },
  component: InsightPage,
});

function InsightPage() {
  const { slug } = Route.useParams();
  const entry = getInsight(slug);

  if (!entry || !insightSlugs.includes(slug)) throw notFound();

  const { Body } = entry;
  return (
    <InsightShell meta={entry.meta}>
      <Body />
    </InsightShell>
  );
}
